import Vue from 'vue';
import { defineStore } from 'pinia';
import { useMainStore } from './mainStore';
import { useUserStore } from './user';
import { axios } from '@/plugins/http-axios';

interface IDataColor {
  [key: string]: string;
}

interface IColorState {
  color: IDataColor;
}

export const useColorStore = defineStore('color', {
  state: (): IColorState => ({
    color: {},
  }),

  getters: {
    typeColorSchema(): 'dark' | 'light' {
      return Vue.prototype.$vuetify.framework.theme.dark ? 'dark' : 'light';
    },
    getThemeObject(): (theme: 'dark' | 'light') => IDataColor {
      // Инициализация темы по-умолчанию происходит в '@/plugins/vuetify.js' и в блоке style App.vue
      return (theme: 'dark' | 'light'): IDataColor => Vue.prototype.$vuetify.framework.theme.themes[theme];
    },
    theme(): IDataColor {
      return this.getThemeObject(this.typeColorSchema);
    },
    logoLoginImgW: (state): string => state.color.LKB_LOGO_IMAGE_WIDTH,
    logoLoginImgH: (state): string => state.color.LKB_LOGO_IMAGE_HEIGHT,
    headerLogoImgW: (state): string => state.color.LKB_HEADER_IMG_WIDTH,
    headerLogoImgH: (state): string => state.color.LKB_HEADER_IMG_HEIGHT,
    MIRROR_REFLECTION: (state): string => state.color.MIRROR_REFLECTION,
    HIDE_FIELD_SHADOW_LOGO: (state): string => state.color.HIDE_FIELD_SHADOW_LOGO,
    DISPLAY_ABOVE_BLOCK: (state): string => state.color.DISPLAY_ABOVE_BLOCK,
    dialogWhiteTextEnable: (state): boolean => state.color.DIALOG_COLOR_TEXT_WHITE === 'true',
  },

  actions: {
    async getDesignConfig() {
      const mainStore = useMainStore();
      const { data } = await axios.get<IDataColor>(mainStore.getUri.designConfig);
      this.color = data;
    },

    initTypeColorSchema() {
      const userStore = useUserStore();
      Vue.prototype.$vuetify.framework.theme.dark = userStore.user.schemaDesign === 'DARK';
    },
    setTheme() {
      let vuetify = this.getThemeObject('light');
      const light = {
        primary: this.color.LKB_BG_COLOR_MAIN_WINDOW_LIGHT || vuetify.primary,
        btn_def: this.color.LKB_BTN_MAIN_COLOR_LIGHT || vuetify.btn_def,
        header: this.color.LKB_HEADER_COLOR_LIGHT || vuetify.header,
        sidebar: this.color.LKB_SIDEBAR_BG_COLOR_LIGHT,
        icon_def: this.color.LKB_ICONS_MAIN_COLOR_LIGHT,

        lkbBackgroundMainColor: this.color.LKB_BG_MAIN_COLOR_LIGHT,
        lkbHeaderFontColor: this.color.LKB_HEADER_FONT_COLOR_LIGHT,

        //todo Скорее всего в будущем нужно эти четыре цвета унифицировать до одного
        //Цвета элементов управления:  input, checkbox, radiobutton, select, textarea
        checkColor: this.color.FORM_CONTROLS_COLOR || vuetify.checkColor,
        radioColor: this.color.FORM_CONTROLS_COLOR || vuetify.radioColor,
        selectColor: this.color.FORM_CONTROLS_COLOR || vuetify.selectColor,
        inputColor: this.color.FORM_CONTROLS_COLOR || vuetify.inputColor,

        loginTextCol: this.color.LKB_COLOR_TEXTS_LOGIN_LIGHT,
        loginBtnCol: this.color.LKB_COLOR_ELEMENTS_LOGIN_LIGHT,
        LkbBgColorEntryPlate: this.color.LKB_BG_COLOR_ENTRY_PLATE_LIGHT,
        LkbBgColorLoginButton: this.color.LKB_BG_COLOR_LOGIN_BUTTON_LIGHT,
        LkbColorSupport: this.color.LKB_COLOR_SUPPORT_LIGHT,
        LkbColorTextsAndElement: this.color.LKB_COLOR_TEXTS_AND_ELEMENTS_LIGHT,
        lkbColorTableHeader: this.color.LKB_COLOR_TABLE_HEADER_LIGHT,
        LkbColorLoginElement: this.color.LKB_COLOR_TEXTS_AND_ELEMENTS_LOGIN_LIGHT,
        LkbColorDownlIcon: this.color.LKB_COLOR_DOC_DOWNLOAD_PICTURE_LIGHT,
        LkbColorGenerateIcon: this.color.LKB_COLOR_DOC_GENERATE_PICTURE_LIGHT,
        LkbColorTextDownloadPicture: this.color.LKB_COLOR_TEXT_DOWNLOAD_PICTURE_LIGHT,
        LkbColorTextGeneratePicture: this.color.LKB_COLOR_TEXT_GENERATE_PICTURE_LIGHT,

        ordinaryButton: this.color.LKB_BUTTON_COLOR_LIGHT,
        burgerApplicationMenu: this.color.BURGER_COLOR_IN_THE_APPLICATION_LIGHT,
      };
      let colors = Object.assign({}, vuetify, light);
      Vue.prototype.$vuetify.framework.theme.themes.light = colors;

      vuetify = this.getThemeObject('dark');
      const dark = {
        primary: this.color.LKB_BG_COLOR_MAIN_WINDOW_DARK,
        btn_def: this.color.LKB_BTN_MAIN_COLOR_DARK || vuetify.btn_def,
        header: this.color.LKB_HEADER_COLOR_DARK,
        lkbHeaderFontColor: this.color.LKB_HEADER_FONT_COLOR_DARK,
        sidebar: this.color.LKB_SIDEBAR_BG_COLOR_DARK,
        icon_def: this.color.LKB_ICONS_MAIN_COLOR_DARK,

        lkbBackgroundMainColor: this.color.LKB_BG_MAIN_COLOR_DARK,
        loginTextCol: this.color.LKB_COLOR_TEXTS_LOGIN_DARK,
        loginBtnCol: this.color.LKB_COLOR_ELEMENTS_LOGIN_DARK,
        LkbBgColorEntryPlate: this.color.LKB_BG_COLOR_ENTRY_PLATE_DARK,
        LkbBgColorLoginButton: this.color.LKB_BG_COLOR_LOGIN_BUTTON_DARK,
        LkbColorSupport: this.color.LKB_COLOR_SUPPORT_DARK,
        LkbColorTextsAndElement: this.color.LKB_COLOR_TEXTS_AND_ELEMENTS_DARK,
        lkbColorTableHeader: this.color.LKB_COLOR_TABLE_HEADER_DARK,
        LkbColorLoginElement: this.color.LKB_COLOR_TEXTS_AND_ELEMENTS_LOGIN_DARK,
        LkbColorDownlIcon: this.color.LKB_COLOR_DOC_DOWNLOAD_PICTURE_DARK,
        LkbColorGenerateIcon: this.color.LKB_COLOR_DOC_GENERATE_PICTURE_DARK,
        LkbColorTextDownloadPicture: this.color.LKB_COLOR_TEXT_DOWNLOAD_PICTURE_DARK,
        LkbColorTextGeneratePicture: this.color.LKB_COLOR_TEXT_GENERATE_PICTURE_DARK,

        ordinaryButton: this.color.LKB_BUTTON_COLOR_DARK,
        burgerApplicationMenu: this.color.BURGER_COLOR_IN_THE_APPLICATION_DARK,
      };

      //@ts-ignore
      colors = Object.assign({}, vuetify, dark);
      Vue.prototype.$vuetify.framework.theme.themes.dark = colors;
    },
    /*
        getDesignScheme({dispatch}) {
            return axios.get(uri.getSchema)
                .then(resp => {
                    if (/dark/i.test(resp.data))
                        Vue.prototype.$vuetify.framework.theme.dark = true;
                    return true;
                })
                .catch(e => {
                    e.uri = uri.getSchema;
                    dispatch('setError', e);
                    return Promise.reject(e);
                });
        },
*/
  },
});

export default useColorStore;
